/**
 * Backend client.
 *
 * The session travels in an httpOnly cookie, so no token ever passes through
 * here -- which is why every call carries `credentials: "include"` and there is
 * no authorization header to assemble.
 */

export interface User {
  id: string;
  email: string;
}

export interface AuditBand {
  mm: number;
  thinPct: number;
}

export interface SavedDesign {
  id: string;
  name: string;
  mode: "cut" | "draw";
  mmWidth: number;
  mmHeight: number;
  targetMm: number | null;
  rotation: number;
  /** outline only: traced as a filled silhouette */
  outline: boolean;
  /** null when outline is off */
  closeGapsMm: number | null;
  largestOnly: boolean;
  /** outline only: how far the piece grows around the drawing; 0 = edge to edge, null when outline is off */
  outlineMarginMm: number | null;
  audit: AuditBand[];
  createdAt: string;
  /** only present when fetching one design; the listing omits it because it is heavy */
  svg?: string;
}

export interface NewDesign {
  name: string;
  mode: "cut" | "draw";
  mmWidth: number;
  mmHeight: number;
  targetMm: number | null;
  rotation: number;
  /** outline only: traced as a filled silhouette */
  outline: boolean;
  /** null when outline is off */
  closeGapsMm: number | null;
  largestOnly: boolean;
  /** outline only: how far the piece grows around the drawing; 0 = edge to edge, null when outline is off */
  outlineMarginMm: number | null;
  svg: string;
  audit: AuditBand[];
}

/**
 * Error carrying the backend's stable code.
 *
 * `code` is what the UI translates; `message` is the server's English text,
 * kept only as a fallback for the rare failure that arrives without a code
 * (a proxy error page, a 502 from the ingress).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(BASE + path, { credentials: "include", ...init });
  } catch {
    throw new ApiError("Could not reach the server.", 0, "network");
  }

  if (res.ok) return res;

  let message = `Something went wrong (${res.status}).`;
  let code: string | undefined;
  try {
    const body = (await res.clone().json()) as { error?: unknown; code?: unknown };
    if (typeof body.error === "string" && body.error) message = body.error;
    if (typeof body.code === "string" && body.code) code = body.code;
  } catch {
    // The body was not JSON; the generic message stands.
  }

  const retry = Number(res.headers.get("Retry-After"));
  throw new ApiError(
    message,
    res.status,
    code,
    Number.isFinite(retry) && retry > 0 ? retry : undefined,
  );
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  return (await request(path, init)).json() as Promise<T>;
}

const jsonBody = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const api = {
  login: (email: string, password: string) =>
    json<User>("/auth/login", jsonBody({ email, password })),

  logout: async () => {
    await request("/auth/logout", { method: "POST" });
  },

  me: () => json<User>("/auth/me"),

  forgotPassword: (email: string) =>
    json<{ message: string }>("/auth/forgot-password", jsonBody({ email })),

  resetPassword: (token: string, password: string) =>
    json<{ message: string }>("/auth/reset-password", jsonBody({ token, password })),

  changePassword: (currentPassword: string, newPassword: string) =>
    json<{ message: string }>("/auth/change-password", jsonBody({ currentPassword, newPassword })),

  /** Sends the raw image and returns the cleaned one. */
  clean: async (file: Blob): Promise<Blob> => {
    const res = await request("/clean", {
      method: "POST",
      headers: { "Content-Type": file.type || "image/png" },
      body: file,
    });
    return res.blob();
  },

  listDesigns: (limit = 50) => json<SavedDesign[]>(`/designs?limit=${limit}`),

  getDesign: (id: string) => json<SavedDesign>(`/designs/${encodeURIComponent(id)}`),

  saveDesign: (d: NewDesign) => json<SavedDesign>("/designs", jsonBody(d)),

  deleteDesign: async (id: string) => {
    await request(`/designs/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  /** Stores the image it was traced from, so the design can be reopened later. */
  putSource: async (id: string, image: Blob) => {
    await request(`/designs/${encodeURIComponent(id)}/source`, {
      method: "PUT",
      headers: { "Content-Type": image.type || "image/png" },
      body: image,
    });
  },

  /** Returns the source image. `null` when the design has none stored. */
  getSource: async (id: string): Promise<Blob | null> => {
    try {
      const res = await request(`/designs/${encodeURIComponent(id)}/source`);
      return await res.blob();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
};
